import CustomInput from "../../../../commonComponents/CustomInput";

import AiSparkleIcon from "../../../../assets/icons/onboarding/ai-sparkle.svg";
import {
  formatReviewFieldDisplayValue,
  listedHrEditFieldKey,
} from "../../../../utils/onboardingHelpers";
import { useHrDirectEditContext } from "../../HrDirectEditContext";
import HrFieldEditor from "../HrFieldEditor";
import { HrEditedByBadge, HrFieldApiHistoryPanel } from "../HrEditHistory";

const ANY_VALUE = /^[\s\S]*$/;

export const getFlagKey = (sectionKey, fieldKey, recordIndex) =>
  recordIndex == null
    ? `${sectionKey}-${fieldKey}`
    : `${sectionKey}-${recordIndex}-${fieldKey}`;

/** AI fields that still support Flagged/Correction. More AI icon is unchanged. */
const FLAGGABLE_AI_FIELD_KEYS = new Set([
  "father_first_name",
  "father_middle_name",
  "father_last_name",
]);

export const isFlagBlockedField = (field) => {
  if (field?.isSystem) return true;
  if (field?.isAi && !FLAGGABLE_AI_FIELD_KEYS.has(field?.key)) return true;
  return false;
};

export const toDocumentFlagField = (doc) => ({
  key: doc?.id,
  label: doc?.name || "Document",
  isDocument: true,
});

export const getUpdatedFieldEntry = (
  updatedFields,
  sectionKey,
  fieldKey,
  recordIndex = null,
) => {
  if (!Array.isArray(updatedFields) || updatedFields.length === 0) return null;
  return (
    updatedFields.find((item) => {
      if (item?.sectionKey !== sectionKey || item?.fieldKey !== fieldKey) {
        return false;
      }
      if (item.recordIndex == null) return true;
      return item.recordIndex === recordIndex;
    }) || null
  );
};

export const isUpdatedReviewField = (
  updatedFields,
  sectionKey,
  fieldKey,
  recordIndex = null,
) => !!getUpdatedFieldEntry(updatedFields, sectionKey, fieldKey, recordIndex);

export const isNameFieldGroup = (fields, index) =>
  fields[index]?.key === "first_name" &&
  fields[index + 1]?.key === "middle_name" &&
  fields[index + 2]?.key === "last_name";

export const isFatherFieldGroup = (fields, index) =>
  fields[index]?.key === "father_first_name" &&
  fields[index + 1]?.key === "father_middle_name" &&
  fields[index + 2]?.key === "father_last_name";

/**
 * Shared flag shell for form fields and document cards.
 */
export const FlaggableWrap = ({
  flagKey,
  canFlag,
  isFlagged,
  isModalOpenForField,
  focusedFlagKey,
  isFilterDisabled,
  showUpdatedHighlight,
  fieldFlag,
  onOpenFlagModal,
  className = "",
  showFlaggedIcon = true,
  showFlagComment = true,
  fieldNote = "",
  children,
}) => {
  return (
    <div
      data-rr-flag-key={flagKey}
      className={`rr-field-wrap ${canFlag ? "rr-field-flaggable" : ""} ${
        isFlagged ? "rr-field-flagged" : ""
      } ${isModalOpenForField ? "rr-field-flag-open" : ""} ${
        focusedFlagKey === flagKey ? "rr-field-focus-target" : ""
      } ${isFilterDisabled ? "rr-field-filter-disabled" : ""} ${
        showUpdatedHighlight ? "rr-field-updated" : ""
      } ${className}`.trim()}
    >
      <div className="rr-field-input-area">
        {showUpdatedHighlight ? (
          <span className="rr-field-updated-badge">Updated</span>
        ) : null}
        {children}
        {isFlagged && !isFilterDisabled && showFlaggedIcon ? (
          <span
            className="material-symbols-rounded rr-field-flagged-icon"
            aria-hidden="true"
          >
            flag
          </span>
        ) : null}
        {canFlag ? (
          <button
            type="button"
            className="rr-field-flag-overlay"
            onClick={onOpenFlagModal}
          >
            <span
              className="material-symbols-rounded rr-field-flag-overlay__icon"
              aria-hidden="true"
            >
              flag
            </span>
            {isFlagged ? "Edit Flag" : "Add Flag"}
          </button>
        ) : null}
      </div>
      {fieldNote ? (
        <div className="rr-field-verification-note">{fieldNote}</div>
      ) : null}
      {isFlagged && !isFilterDisabled && showFlagComment ? (
        <div className="rr-field-flag-comment">{fieldFlag.remark}</div>
      ) : null}
    </div>
  );
};

/**
 * Shared review field renderer used by Personal / Address / Education / etc.
 */
const ReviewField = ({
  sectionKey,
  field,
  formData,
  fieldFlags,
  flagModeEnabled,
  restrictToUpdatedFields,
  updatedFields,
  flagModal,
  focusedFlagKey,
  onOpenFlagModal,
  onUpdateField,
  options = {},
}) => {
  const {
    isNameField = false,
    recordIndex = null,
    recordLabel = "",
    labelOverride = "",
    fieldNote = "",
  } = options;

  const flagKey = getFlagKey(sectionKey, field.key, recordIndex);
  const isUpdatedField = isUpdatedReviewField(
    updatedFields,
    sectionKey,
    field.key,
    recordIndex,
  );
  const isFilterDisabled = restrictToUpdatedFields && !isUpdatedField;
  const showUpdatedHighlight = restrictToUpdatedFields && isUpdatedField;
  const canFlag =
    flagModeEnabled &&
    !isFlagBlockedField(field) &&
    (!restrictToUpdatedFields || isUpdatedField);
  const fieldFlag = !isFlagBlockedField(field) ? fieldFlags[flagKey] : null;
  const isFlagged = !!fieldFlag;
  const isModalOpenForField = canFlag && flagModal?.flagKey === flagKey;
  const displayLabel = labelOverride || field.label;
  const hrEdit = useHrDirectEditContext();
  const hrSession = hrEdit?.session;
  const savedFieldValue =
    sectionKey === "employment" || sectionKey === "education"
      ? recordIndex != null
        ? formData?.[sectionKey]?.records?.[recordIndex]?.[field.key] || ""
        : formData?.[sectionKey]?.[field.key] || ""
      : formData?.[sectionKey]?.[field.key] || "";
  const sameHrRecord =
    hrSession?.kind === "field" &&
    hrSession.sectionKey === sectionKey &&
    (hrSession.recordIndex ?? null) === (recordIndex ?? null) &&
    hrSession.fieldKey !== field.key &&
    Object.prototype.hasOwnProperty.call(hrSession.values || {}, field.key);
  const fieldValue = sameHrRecord ? hrSession.values[field.key] ?? "" : savedFieldValue;
  const hrEditable = !!hrEdit?.isFieldEditable?.(sectionKey, field.key);
  const isHrEditing =
    hrSession?.kind === "field" &&
    hrSession.sectionKey === sectionKey &&
    hrSession.fieldKey === field.key &&
    (hrSession.recordIndex ?? null) === (recordIndex ?? null);
  const fieldHistory = hrEdit?.fieldHistory?.(sectionKey, field.key, recordIndex) || [];
  const listedHistoryKey = listedHrEditFieldKey(
    hrEdit?.hrEditedFields,
    sectionKey,
    field.key,
    recordIndex,
  );
  const historyFieldKey = listedHistoryKey || field.key;
  const showHrEdited =
    (fieldHistory.length > 0 || !!listedHistoryKey) && !isHrEditing;
  const historyOpen = !!hrEdit?.isHistoryOpen?.(
    sectionKey,
    historyFieldKey,
    recordIndex,
  );
  const apiHistoryState = historyOpen
    ? hrEdit?.getHistoryState?.(sectionKey, historyFieldKey, recordIndex)
    : null;
  const showHrLock = !!hrEdit?.enabled && !hrEditable && !field.isSystem && field.key;

  return (
    <FlaggableWrap
      flagKey={flagKey}
      canFlag={canFlag && !isHrEditing}
      isFlagged={isFlagged}
      isModalOpenForField={isModalOpenForField}
      focusedFlagKey={focusedFlagKey}
      isFilterDisabled={isFilterDisabled}
      showUpdatedHighlight={showUpdatedHighlight}
      fieldFlag={fieldFlag}
      fieldNote={fieldNote}
      showFlaggedIcon={!isHrEditing}
      showFlagComment={!isHrEditing}
      onOpenFlagModal={(event) =>
        onOpenFlagModal(
          sectionKey,
          { ...field, label: displayLabel },
          event,
          {
            recordIndex,
            recordLabel,
          },
        )
      }
      className={`${field.isAi ? "rr-field-ai" : ""} ${
        field.isSystem ? "rr-field-system" : ""
      } ${showHrLock ? "rr-field-hr-locked" : ""} ${
        field.isMultiline ? "rr-field-multiline" : ""
      } ${isNameField ? "rr-field-name" : ""} ${
        field.fullWidth ? "rr-address-field-full" : ""
      } ${isHrEditing ? "rr-field-hr-editing" : ""
      } ${showHrEdited ? "rr-field-hr-edited" : ""}`}
    >
      {hrEditable && !isHrEditing && !isFilterDisabled ? (
        <button
          type="button"
          className="rr-hr-edit-btn"
          aria-label={`Edit ${displayLabel}`}
          title={`Edit ${displayLabel}`}
          onClick={() =>
            hrEdit.startFieldEdit({
              sectionKey,
              fieldKey: field.key,
              recordIndex,
              label: displayLabel,
              isRequired: !!field.isRequired,
            })
          }
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            edit
          </span>
        </button>
      ) : null}
      {showHrEdited ? (
        <div className="rr-field-hr-label-row">
          <span className="emp-form-label">{displayLabel}</span>
          <HrEditedByBadge
            entries={fieldHistory}
            visible={!!listedHistoryKey}
            expanded={historyOpen}
            onClick={() =>
              hrEdit?.toggleFieldHistory?.({
                sectionKey,
                fieldKey: historyFieldKey,
                recordIndex,
              })
            }
          />
        </div>
      ) : null}
      {isHrEditing ? (
        <HrFieldEditor
          session={hrSession}
          flagRemark={isFlagged && !isFilterDisabled ? fieldFlag.remark : null}
          onChangeValue={hrEdit.updateDraftValue}
          onApplyPincode={hrEdit.applyPincodeDetails}
          onChangeReason={hrEdit.updateReason}
          onCancel={hrEdit.cancelEdit}
          onSave={hrEdit.saveEdit}
        />
      ) : (
      <div className={showHrEdited ? "rr-field-hr-edited-input" : undefined}>
      <CustomInput
        label={showHrEdited ? "" : displayLabel}
        value={formatReviewFieldDisplayValue(field.key, fieldValue)}
        placeholder={`Enter ${String(displayLabel).toLowerCase()}`}
        isRequired={!!field.isRequired}
        editable={false}
        readOnly={true}
        preview={true}
        validationStr={ANY_VALUE}
        onChange={(value) =>
          onUpdateField(sectionKey, field.key, value, recordIndex)
        }
      />
      </div>
      )}
      {historyOpen && !isHrEditing ? (
        <HrFieldApiHistoryPanel state={apiHistoryState} fieldKey={field.key} />
      ) : null}
      {field.isAi && !isFilterDisabled && (
        <img
          className="rr-ai-icon"
          src={AiSparkleIcon}
          alt="AI populated"
          title="Populated by AI"
        />
      )}
      {showHrLock && !isFilterDisabled && (
        <span
          className="material-symbols-rounded rr-hr-lock-icon"
          title="Locked"
          aria-label="Locked"
        >
          lock
        </span>
      )}
      {field.isSystem && !isFilterDisabled && (
        <span
          className="material-symbols-rounded rr-system-lock-icon"
          title="System generated"
          aria-label="System generated"
        >
          lock
        </span>
      )}
    </FlaggableWrap>
  );
};

export default ReviewField;
